import { Suspense, useRef, useState, useEffect } from "react";
import { Canvas, useThree } from "@react-three/fiber";
import { OrbitControls, Bounds, Html, Environment, Lightformer, TransformControls } from "@react-three/drei";
import * as THREE from "three";
import api, { apiError, assetUrl } from "@/lib/api";
import { Part3D } from "@/pages/Part3D";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Cube, UploadSimple, CircleNotch, Trash, ArrowsOutCardinal, ArrowClockwise, ArrowsOut, ArrowsIn, Cylinder, Circle, Sphere, Copy, MagicWand, Crosshair, Eye, Images, Sparkle, TrafficCone, Triangle, Polygon, ArrowArcLeft, ArrowArcRight, LinkSimple, LinkBreak, CaretDoubleLeft, CaretDoubleRight, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";
import { MATERIAL_FAMILIES, FAMILY_ORDER } from "@/lib/materials";
import { STONE_SHAPES } from "@/lib/stoneShapes";
import { getGlbThumb } from "@/lib/glbThumb";
import { useTranslation } from "react-i18next";

const CHUNK = 4 * 1024 * 1024;
const ROLES = ["band", "logo", "stones", "enamel"];

const asScale3 = (s) => (Array.isArray(s) ? s : [s || 1, s || 1, s || 1]);
const ROLE_COLOR = { band: "#C0C0C0", logo: "#d4af37", stones: "#8fd0ff", enamel: "#C81E3A" };

function PartRowThumb({ part }) {
  const [src, setSrc] = useState(null);
  const url = part.model_url || (part.asset_id ? `/api/assets/${part.asset_id}` : null);
  const color = part.color || ROLE_COLOR[part.role] || "#C0C0C0";
  const orientKey = (part.orient || []).join(",");
  useEffect(() => {
    let alive = true;
    setSrc(null);
    if (url) getGlbThumb(url, part.orient, color).then((u) => { if (alive && u) setSrc(u); }).catch(() => {});
    return () => { alive = false; };
  }, [url, color, orientKey]); // eslint-disable-line
  return (
    <div className="w-9 h-9 rounded border border-white/15 bg-neutral-800 shrink-0 overflow-hidden flex items-center justify-center" data-testid={`part-thumb-${part.id}`}>
      {src ? <img src={src} alt="" className="w-full h-full object-contain" /> : <span className="w-3.5 h-3.5 rounded-full" style={{ background: color }} />}
    </div>
  );
}

function EditablePart({ part, selected, mode, dim, onCommit, onRef, onSelect, marked, placeMode, onPlaceClick, mainFamily, onDragStart, onDragEnd }) {
  const { t } = useTranslation();
  const ref = useRef();
  const start = useRef(null);
  useEffect(() => {
    onRef(part.id, ref.current);
    return () => onRef(part.id, null);
  }, [part.id, part.model_url, part.primitive, part.centered]); // eslint-disable-line
  const begin = () => {
    const o = ref.current; if (!o) return;
    start.current = { position: [o.position.x, o.position.y, o.position.z], rotation: [o.rotation.x, o.rotation.y, o.rotation.z], scale: [o.scale.x, o.scale.y, o.scale.z] };
  };
  const commit = () => {
    const o = ref.current; if (!o) return;
    onCommit({ position: [o.position.x, o.position.y, o.position.z], rotation: [o.rotation.x, o.rotation.y, o.rotation.z], scale: [o.scale.x, o.scale.y, o.scale.z] }, start.current);
  };
  const color = part.color || ROLE_COLOR[part.role] || "#C0C0C0";
  // Non-metal material (per-part override or the product's main material) → matte look.
  const fam = part.material || (["band", "logo", "metal"].includes(part.role) ? mainFamily : null);
  const mp = fam && fam !== "metal" && MATERIAL_FAMILIES[fam] ? MATERIAL_FAMILIES[fam] : null;
  return (
    <>
      <group ref={ref} position={part.position || [0, 0, 0]} rotation={part.rotation || [0, 0, 0]} scale={asScale3(part.scale)}
        onClick={(e) => {
          e.stopPropagation();
          // In place mode a single click drops the selected part onto this surface.
          if (placeMode && !selected && onPlaceClick) { onPlaceClick(e); return; }
          // Otherwise a single click does NOT change the selection: this prevents another
          // part from being grabbed by accident when the cursor lands on it after a
          // move/scale drag. Use DOUBLE-CLICK (below) to pick a part.
        }}
        onDoubleClick={(e) => {
          e.stopPropagation();
          if (placeMode) return;
          onSelect(part.id, e.nativeEvent?.shiftKey || e.shiftKey);
        }}
        onPointerOver={(e) => { e.stopPropagation(); document.body.style.cursor = (placeMode && !selected) ? "crosshair" : "pointer"; }}
        onPointerOut={() => { document.body.style.cursor = "default"; }}>
        <Part3D part={part} colorOverride={marked ? "#22d3ee" : color} metalnessOverride={mp?.metalness} roughnessOverride={mp?.roughness} opacity={dim ? 0.18 : 1} centered={part.centered} />
        {selected && !marked && (
          <group scale={asScale3(part.scale).map((v) => 1 / (Math.abs(v) || 1))}>
            <mesh position={[0, 0, 0.6]} rotation={[Math.PI / 2, 0, 0]}>
              <coneGeometry args={[0.09, 0.24, 4]} />
              <meshBasicMaterial color="#22c55e" depthTest={false} />
            </mesh>
            <Html center position={[0, 0.28, 0.6]}>
              <div style={{ background: "#22c55e", color: "#000", borderRadius: 6, padding: "1px 7px", fontSize: 10, fontWeight: 800, whiteSpace: "nowrap", pointerEvents: "none", border: "1px solid #000" }}>{t("parts.front")}</div>
            </Html>
          </group>
        )}
        {marked && (
          <Html center>
            <div style={{ background: "#22d3ee", color: "#000", borderRadius: "9999px", padding: "1px 6px", fontSize: 11, fontWeight: 800, border: "1px solid #000", whiteSpace: "nowrap", pointerEvents: "none" }}>✓</div>
          </Html>
        )}
      </group>
      {selected && !marked && !placeMode && <TransformControls object={ref} mode={mode} size={0.85} onMouseDown={() => { begin(); onDragStart && onDragStart(); }} onMouseUp={() => { commit(); onDragEnd && onDragEnd(); }} />}
    </>
  );
}

function ViewController({ viewRef }) {
  const { camera, controls } = useThree();
  useEffect(() => {
    viewRef.current = (dir) => {
      if (!controls) return;
      const target = controls.target.clone();
      const dist = camera.position.distanceTo(target) || 4;
      const dirs = { front: [0, 0, 1], back: [0, 0, -1], right: [1, 0, 0], left: [-1, 0, 0], top: [0, 1, 0], bottom: [0, -1, 0] };
      const v = dirs[dir] || dirs.front;
      camera.up.set(dir === "top" || dir === "bottom" ? 0 : 0, dir === "top" || dir === "bottom" ? 0 : 1, dir === "top" ? -1 : dir === "bottom" ? 1 : 0);
      camera.position.set(target.x + v[0] * dist, target.y + v[1] * dist, target.z + v[2] * dist);
      camera.lookAt(target);
      controls.update();
    };
    return () => { viewRef.current = null; };
  }, [camera, controls, viewRef]);
  return null;
}

export default function Model3DParts({ pid, parts, onChange, onUndo, onRedo, canUndo, canRedo, materialFamily = "metal" }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [selId, setSelId] = useState(null);
  const [mode, setMode] = useState("translate");
  const [fullscreen, setFullscreen] = useState(false);
  const [frameKey, setFrameKey] = useState(0);
  const [isolate, setIsolate] = useState(true);
  const [placeMode, setPlaceMode] = useState(false);
  const sceneGroupRef = useRef();
  const draggingRef = useRef(false);
  const [libOpen, setLibOpen] = useState(false);
  const [lib3d, setLib3d] = useState([]);
  const [menuOpen, setMenuOpen] = useState(true);
  const [groupSel, setGroupSel] = useState([]);
  const fileRef = useRef();
  const splitFileRef = useRef();
  const partObjs = useRef({});
  const viewRef = useRef(null);
  const setView = (d) => viewRef.current && viewRef.current(d);
  const onRef = (id, obj) => { if (obj) partObjs.current[id] = obj; else delete partObjs.current[id]; };

  const toggleGroupSel = (id) => setGroupSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectPart = (id, shift) => {
    if (shift) {
      setGroupSel((s) => {
        const next = s.includes(id) ? s.filter((x) => x !== id) : [...s, id];
        toast.success(t("parts.t_marked", { n: next.length }));
        return next;
      });
    } else {
      setSelId(id);
    }
  };
  const groupSelected = () => {
    if (groupSel.length < 2) return;
    const gid = "g" + Math.random().toString(36).slice(2, 8);
    onChange((parts || []).map((p) => groupSel.includes(p.id) ? { ...p, group_id: gid } : p));
    toast.success(t("parts.t_grouped", { n: groupSel.length }));
    setGroupSel([]);
  };
  const ungroup = (p) => {
    const gid = p.group_id; if (!gid) return;
    onChange((parts || []).map((x) => x.group_id === gid ? { ...x, group_id: null } : x));
    toast.success(t("parts.t_ungrouped"));
  };

  // Apply a gizmo commit; if the moved part belongs to a group, propagate the delta
  // (translation/rotation) and scale ratio to the other members so they move together.
  const applyTransform = (id, t, startT) => {
    const part = (parts || []).find((x) => x.id === id);
    const gid = part?.group_id;
    if (!gid || !startT) { updPart(id, t); return; }
    const dp = t.position.map((v, i) => v - (startT.position[i] || 0));
    const dr = t.rotation.map((v, i) => v - (startT.rotation[i] || 0));
    const sr = t.scale.map((v, i) => (startT.scale[i] || 1) ? v / (startT.scale[i] || 1) : 1);
    onChange((parts || []).map((x) => {
      if (x.id === id) return { ...x, ...t };
      if (x.group_id === gid) {
        const pos = x.position || [0, 0, 0]; const rot = x.rotation || [0, 0, 0]; const sc = asScale3(x.scale);
        return { ...x, position: [pos[0] + dp[0], pos[1] + dp[1], pos[2] + dp[2]],
          rotation: [rot[0] + dr[0], rot[1] + dr[1], rot[2] + dr[2]],
          scale: [sc[0] * sr[0], sc[1] * sr[1], sc[2] * sr[2]] };
      }
      return x;
    }));
  };

  const worldBox = (id) => {
    const o = partObjs.current[id];
    if (!o) return null;
    const box = new THREE.Box3().setFromObject(o);
    if (box.isEmpty()) return null;
    const size = new THREE.Vector3(); box.getSize(size);
    const center = new THREE.Vector3(); box.getCenter(center);
    return { size, center };
  };

  const toggleCentered = (p) => {
    if (!p.centered) {
      const wb = worldBox(p.id);
      if (!wb) { updPart(p.id, { centered: true }); return; }
      const s = asScale3(p.scale);
      const pos = p.position || [0, 0, 0];
      const offset = [(wb.center.x - pos[0]) / (s[0] || 1), (wb.center.y - pos[1]) / (s[1] || 1), (wb.center.z - pos[2]) / (s[2] || 1)];
      updPart(p.id, { centered: true, center_offset: offset, position: [wb.center.x, wb.center.y, wb.center.z] });
    } else {
      const s = asScale3(p.scale);
      const off = p.center_offset || [0, 0, 0];
      const pos = p.position || [0, 0, 0];
      updPart(p.id, { centered: false, position: [pos[0] - s[0] * off[0], pos[1] - s[1] * off[1], pos[2] - s[2] * off[2]] });
    }
    toast.success(p.centered ? t("parts.t_scaleNormal") : t("parts.t_scaleCenter"));
  };

  const realCurve = (p) => {
    const band = (parts || []).find((x) => x.role === "band" || (x.slot || "base") === "base");
    if (!band || band.id === p.id) { toast.error(t("parts.t_needBand")); return; }
    const bb = worldBox(band.id);
    const lb = worldBox(p.id);
    if (!bb || !lb) { toast.error(t("parts.t_cantMeasure")); return; }
    const R = Math.max(bb.size.x, bb.size.y) / 2 || 1;   // radio aproximado de la banda
    const axis = lb.size.x >= lb.size.y ? "x" : "y";
    const W = axis === "x" ? lb.size.x : lb.size.y;       // ancho del logo en el eje de curvado
    let bend = W / (Math.PI * R);
    bend = Math.max(-1, Math.min(1, bend));
    updPart(p.id, { bend: parseFloat(bend.toFixed(3)), bendAxis: axis, position: [bb.center.x, bb.center.y + R, bb.center.z] });
    toast.success(t("parts.t_realCurveApplied", { r: R.toFixed(2) }));
  };

  const openLibrary = async () => {
    setLibOpen(true);
    try { const { data } = await api.get("/logos"); setLib3d(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const [libBusy, setLibBusy] = useState(false);
  const addLibraryLogo = async (logo) => {
    let modelAid = logo.model3d_asset_id;
    let modelUrl = logo.model_url;
    if (!modelAid) {
      // 2D logo: generate its 3D relief on the fly
      setLibBusy(true);
      try {
        const { data } = await api.post("/logos3d/generate", { source: "image", image_asset_id: logo.asset_id, name: logo.name });
        modelAid = data.model_asset_id; modelUrl = data.model_url;
      } catch (e) { toast.error(apiError(e)); setLibBusy(false); return; }
      setLibBusy(false);
    }
    const id = Math.random().toString(36).slice(2, 10);
    const prepared = !!logo.prepared;
    const part = prepared
      ? { id, name: logo.name || "Logo 3D", role: "logo", slot: "center", prepared: true,
          asset_id: modelAid, model_url: modelUrl, thumb_url: logo.url || null,
          position: [0, 0, 0], rotation: [0, 0, 0], scale: [1, 1, 1], color: "#C0C0C0",
          metalness: 0.85, roughness: 0.3, optional: true, normalize: false }
      : { id, name: logo.name || "Logo 3D", role: "logo", slot: "center",
          asset_id: modelAid, model_url: modelUrl, thumb_url: logo.url || null, orient: logo.orient || null,
          position: [0, 0, 0], rotation: [0, 0, 0], scale: [0.6, 0.6, 0.6], color: "#C0C0C0",
          metalness: 0.85, roughness: 0.3, optional: true, bend: 0, bendAxis: "x", normalize: true };
    onChange([...(parts || []), part]);
    setSelId(id);
    setLibOpen(false);
    toast.success(t("parts.t_logoAdded"));
  };

  const uploadPart = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["glb", "gltf", "obj", "stl", "ply"].includes(ext)) { toast.error(t("parts.t_unsupported")); return; }
    setBusy(true); setPct(0);
    const uploadId = Math.random().toString(36).slice(2) + Date.now();
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId); fd.append("index", i); fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post(`/products/${pid}/model3d/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPct(Math.round(((i + 1) / total) * 90));
      }
      const name = file.name.replace(/\.[^.]+$/, "").replace(/Meshy_AI_|_image-to-3d-texture|_\d+/g, "").replace(/_/g, " ").trim() || t("parts.piece");
      const { data } = await api.post(`/products/${pid}/model3d/finalize`, { upload_id: uploadId, filename: file.name, as_part: true, name, role: /logo/i.test(file.name) ? "logo" : /stone|piedra/i.test(file.name) ? "stones" : /band|banda/i.test(file.name) ? "band" : "logo", slot: /left|izq/i.test(file.name) ? "left" : /right|der/i.test(file.name) ? "right" : /center|centro/i.test(file.name) ? "center" : /band|banda/i.test(file.name) ? "base" : /stone|piedra/i.test(file.name) ? "stones" : "base" });
      setPct(100);
      onChange([...(parts || []), data.part]);
      toast.success(t("parts.t_partAdded"));
      // Designer logos become reusable library logos automatically (kept exactly as delivered).
      if (data.part?.role === "logo" && data.part?.id) {
        try { await api.post(`/products/${pid}/parts/${data.part.id}/to-collection`); } catch { /* ignore */ }
      }
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  // Auto-layers: upload one multi-mesh file and let the backend split it into
  // separate editable parts, auto-detecting each part's role (band/logo/stones/enamel) by name.
  const uploadSplit = async (file) => {
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["glb", "gltf", "obj", "stl", "ply"].includes(ext)) { toast.error(t("parts.t_unsupported")); return; }
    setBusy(true); setPct(0);
    const uploadId = Math.random().toString(36).slice(2) + Date.now();
    const total = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("upload_id", uploadId); fd.append("index", i); fd.append("chunk", file.slice(i * CHUNK, (i + 1) * CHUNK));
        await api.post(`/products/${pid}/model3d/chunk`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPct(Math.round(((i + 1) / total) * 90));
      }
      const { data } = await api.post(`/products/${pid}/model3d/finalize-split`, { upload_id: uploadId, filename: file.name });
      setPct(100);
      onChange([...(parts || []), ...(data.parts || [])]);
      toast.success(t("parts.t_layersCreated", { n: data.count }));
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const updPart = (id, patch) => onChange(parts.map((p) => p.id === id ? { ...p, ...patch } : p));
  const delPart = (id) => { onChange(parts.filter((p) => p.id !== id)); if (selId === id) setSelId(null); };

  const isBand = (p) => p.role === "band";

  // Click-to-place (layered): stick the SELECTED piece onto the surface you click (base band or an enamel).
  // The surface normal is computed DETERMINISTICALLY as "outward" (away from the ring center ≈ group
  // origin), NOT from the camera, so the piece always sits on the OUTER surface facing out — this fixes
  // logos that ended up facing INSIDE the ring (e.g. the top-right zone).
  const placeOnBand = (e) => {
    const p = (parts || []).find((x) => x.id === selId);
    if (!p) { toast.error(t("parts.t_selectFirst")); return; }
    if (isBand(p)) { toast.error(t("parts.t_bandIsBase")); return; }
    const grp = sceneGroupRef.current;
    if (!grp || !e.point) return;
    const localPoint = grp.worldToLocal(e.point.clone());
    // Face normal in the group's local space (fallback: radial direction from center).
    let localNormal;
    if (e.face && e.object) {
      const wn = e.face.normal.clone().transformDirection(e.object.matrixWorld).normalize();
      const inv = new THREE.Matrix4().copy(grp.matrixWorld).invert();
      localNormal = wn.clone().transformDirection(inv).normalize();
    } else {
      localNormal = localPoint.clone().normalize();
    }
    // Force the normal to point OUTWARD (away from the ring center at the group origin).
    if (localNormal.dot(localPoint) < 0) localNormal.negate();
    const depth = p.reliefDepth ?? (p.role === "logo" ? 0.09 : p.role === "stones" ? 0.08 : 0.04);
    const off = depth / 2 + 0.01;
    const posV = localPoint.clone().add(localNormal.clone().multiplyScalar(off));
    let eul;
    if (p.role === "logo") {
      // Logos: cara legible hacia AFUERA y en vertical. +Z (frente) = normal saliente,
      // +Y = up-mundo proyectado en la superficie. Determinista → todas las zonas quedan igual.
      const n = localNormal.clone();
      let up = new THREE.Vector3(0, 1, 0);
      if (Math.abs(n.dot(up)) > 0.98) up = new THREE.Vector3(0, 0, 1);
      const xAxis = new THREE.Vector3().crossVectors(up, n).normalize();
      const yAxis = new THREE.Vector3().crossVectors(n, xAxis).normalize();
      const basis = new THREE.Matrix4().makeBasis(xAxis, yAxis, n);
      eul = new THREE.Euler().setFromRotationMatrix(basis);
    } else {
      // Esmaltes/piedras: rotación MÍNIMA que apoya su cara (+Z actual) sobre la superficie,
      // conservando su giro/forma (trapecio) tal como lo dejó el usuario.
      const curQ = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(p.rotation || [0, 0, 0])));
      const curForward = new THREE.Vector3(0, 0, 1).applyQuaternion(curQ).normalize();
      const align = new THREE.Quaternion().setFromUnitVectors(curForward, localNormal.clone());
      const newQ = align.multiply(curQ);
      eul = new THREE.Euler().setFromQuaternion(newQ);
    }
    const patch = { position: [posV.x, posV.y, posV.z], rotation: [eul.x, eul.y, eul.z] };
    if (p.role === "logo") { patch.bend = 0; patch.bendX = 0; patch.bendY = 0; }
    updPart(p.id, patch);
    toast.success(t("parts.t_stuck", { name: p.name }));
  };

  const setAnchor = (p, on) => {
    onChange((parts || []).map((x) => {
      if (x.id === p.id) return { ...x, slot_anchor: on };
      if (on && x.slot === p.slot) return { ...x, slot_anchor: false };
      return x;
    }));
    if (on) toast.success(t("parts.t_anchorSet", { name: p.name, slot: p.slot }));
  };

  const addPartToCollection = async (p) => {
    try {
      await api.post(`/products/${pid}/parts/${p.id}/to-collection`);
      toast.success(`"${p.name}" añadido a la colección de logos`);
      try { const { data } = await api.get("/logos"); setLib3d(data); } catch { /* ignore */ }
    } catch (e) { toast.error(apiError(e)); }
  };

  const addPrimitive = (primitive) => {
    const labels = { box: t("parts.box"), sphere: t("parts.sphere"), cylinder: t("parts.cylinder"), torus: t("parts.torus"), gem: t("parts.gem"), cone: t("parts.cone"), pyramid: t("parts.pyramid"), prism: t("parts.prism") };
    const dimsDefault = { cylinder: { radius: 0.5, height: 1 }, torus: { radius: 0.5, tube: 0.18 }, cone: { radius: 0.5, height: 1 }, pyramid: { radius: 0.6, height: 1 }, prism: { radiusTop: 0.5, radiusBottom: 0.5, height: 1, sides: 3 }, gem: { cut: "round" } };
    const id = Math.random().toString(36).slice(2, 10);
    const isGem = primitive === "gem";
    const part = { id, name: labels[primitive], primitive, role: isGem ? "stones" : "logo", slot: isGem ? "stones" : "base",
      position: [0, 0, 0], rotation: [0, 0, 0], scale: isGem ? [0.2, 0.2, 0.2] : [1, 1, 1], color: isGem ? "#e8f4ff" : "#C0C0C0",
      metalness: isGem ? 0.1 : 0.85, roughness: isGem ? 0.02 : 0.3, optional: false, bend: 0, bendAxis: "x",
      taper: 0, shear: 0, dims: dimsDefault[primitive] };
    onChange([...(parts || []), part]);
    setSelId(id);
    toast.success(t("parts.t_primAdded", { name: labels[primitive] }));
  };

  const setScaleAxis = (p, idx, val) => {
    const arr = asScale3(p.scale).slice();
    arr[idx] = parseFloat(val) || 0;
    updPart(p.id, { scale: arr });
  };

  const setDim = (p, key, val) => updPart(p.id, { dims: { ...(p.dims || {}), [key]: parseFloat(val) } });
  const rotOrient = (p, axis, delta) => {
    const o = Array.isArray(p.orient) ? [...p.orient] : [0, 0, 0];
    o[axis] = Math.round(((o[axis] + delta) % (Math.PI * 2)) * 1e6) / 1e6;
    updPart(p.id, { orient: o, autoOrient: false });
  };

  const dupPart = (p) => {
    const id = Math.random().toString(36).slice(2, 10);
    const pos = p.position || [0, 0, 0];
    const copy = {
      ...p, id, name: (p.name || t("parts.piece")) + t("parts.copyName"),
      position: [-(pos[0] || 0), pos[1] || 0, pos[2] || 0],
      slot: p.slot === "left" ? "right" : p.slot === "right" ? "left" : p.slot,
    };
    onChange([...(parts || []), copy]);
    setSelId(id);
    toast.success(t("parts.t_duplicated"));
  };

  const alignToBand = (p) => {
    const band = (parts || []).find((x) => x.role === "band" || (x.slot || "base") === "base");
    const patch = { bend: 0.6, bendAxis: "x" };
    if (band && band.id !== p.id) {
      const bp = band.position || [0, 0, 0];
      patch.position = [p.position?.[0] ?? bp[0], bp[1] || 0, bp[2] || 0];
      patch.rotation = band.rotation || [0, 0, 0];
    }
    updPart(p.id, patch);
    toast.success(t("parts.t_aligned"));
  };

  return (
    <div className={fullscreen ? "fixed inset-0 z-[45] bg-[#0d0d0d] p-4 overflow-auto" : "border border-white/10 rounded-xl p-5 bg-[#121212]"} data-testid="model3d-parts">
      <div className="flex items-center justify-between mb-1">
        <h3 className="font-semibold flex items-center gap-2"><Cube size={18} className="text-gold" /> {t("parts.title")}</h3>
        <input type="file" accept=".glb,.gltf,.obj,.stl,.ply" ref={fileRef} className="hidden" onChange={(e) => { uploadPart(e.target.files[0]); e.target.value = ""; }} />
        <input type="file" accept=".glb,.gltf,.obj,.stl,.ply" ref={splitFileRef} className="hidden" onChange={(e) => { uploadSplit(e.target.files[0]); e.target.value = ""; }} />
        <div className="flex items-center gap-1.5">
          <Button onClick={() => setFullscreen((v) => !v)} size="sm" variant="outline" className="rounded-full border-white/15 h-8" data-testid="fullscreen-toggle">
            {fullscreen ? <><ArrowsIn size={15} className="mr-1" /> {t("parts.exit")}</> : <><ArrowsOut size={15} className="mr-1" /> {t("parts.fullscreen")}</>}
          </Button>
          <Button onClick={openLibrary} size="sm" variant="outline" className="rounded-full border-white/15 h-8" data-testid="add-library-logo-btn"><Images size={15} className="mr-1" /> {t("parts.logo3d")}</Button>
          <Button onClick={() => splitFileRef.current?.click()} disabled={busy} size="sm" variant="outline" className="rounded-full border-cyan-400/40 text-cyan-300 h-8 font-semibold" data-testid="add-split-btn">
            {busy ? <><CircleNotch className="animate-spin mr-1" /> {pct}%</> : <><Stack className="mr-1" /> {t("parts.autoLayers")}</>}
          </Button>
          <Button onClick={() => fileRef.current?.click()} disabled={busy} size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="add-part-btn">
            {busy ? <><CircleNotch className="animate-spin mr-1" /> {pct}%</> : <><UploadSimple className="mr-1" /> {t("parts.uploadPart")}</>}
          </Button>
        </div>
      </div>
      <Dialog open={libOpen} onOpenChange={setLibOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-neutral-200 max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("parts.libTitle")}</DialogTitle></DialogHeader>
          {lib3d.length === 0 ? (
            <p className="text-sm text-neutral-500">{t("parts.libEmpty")}</p>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {lib3d.map((l) => (
                <button key={l.id} onClick={() => addLibraryLogo(l)} className="border border-white/10 rounded-lg overflow-hidden bg-white hover:border-gold" data-testid={`lib-logo-${l.id}`}>
                  <div className="aspect-square p-2 flex items-center justify-center bg-neutral-100"><img src={assetUrl(l.url)} alt={l.name} className="max-w-full max-h-full object-contain" /></div>
                  <div className="px-2 py-1 bg-[#121212] text-[11px] truncate">{l.name}</div>
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      <div className="flex items-center gap-1.5 mb-3">
        <span className="text-xs text-neutral-500 mr-1">{t("parts.addShape")}</span>
        <Button onClick={() => addPrimitive("box")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-box"><Cube size={13} className="mr-1" /> {t("parts.box")}</Button>
        <Button onClick={() => addPrimitive("sphere")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-sphere"><Sphere size={13} className="mr-1" /> {t("parts.sphere")}</Button>
        <Button onClick={() => addPrimitive("cylinder")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-cylinder"><Cylinder size={13} className="mr-1" /> {t("parts.cylinder")}</Button>
        <Button onClick={() => addPrimitive("torus")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-torus"><Circle size={13} className="mr-1" /> {t("parts.torus")}</Button>
        <Button onClick={() => addPrimitive("cone")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-cone"><TrafficCone size={13} className="mr-1" /> {t("parts.cone")}</Button>
        <Button onClick={() => addPrimitive("pyramid")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-pyramid"><Triangle size={13} className="mr-1" /> {t("parts.pyramid")}</Button>
        <Button onClick={() => addPrimitive("prism")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-prism"><Polygon size={13} className="mr-1" /> {t("parts.prism")}</Button>
        <Button onClick={() => addPrimitive("gem")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="add-primitive-gem"><Sparkle size={13} className="mr-1" /> {t("parts.gem")}</Button>
      </div>
      <p className="text-xs text-neutral-500 mb-3">{t("parts.helpText")}</p>

      <div className={fullscreen ? (menuOpen ? "grid lg:grid-cols-[2fr_1fr] gap-4" : "grid grid-cols-1 gap-4") : "grid md:grid-cols-2 gap-4"}>
        <div>
          <div className="flex items-center gap-1.5 mb-2 flex-wrap">
            <Button onClick={() => setFrameKey((k) => k + 1)} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="frame-btn"><Crosshair size={13} className="mr-1" /> {t("parts.frame")}</Button>
            <div className="flex items-center gap-1">
              <Button onClick={() => setView("front")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs px-2.5" data-testid="view-front-btn">{t("parts.viewFront")}</Button>
              <Button onClick={() => setView("right")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs px-2.5" data-testid="view-side-btn">{t("parts.viewSide")}</Button>
              <Button onClick={() => setView("top")} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs px-2.5" data-testid="view-top-btn">{t("parts.viewTop")}</Button>
            </div>
            <Button onClick={onUndo} disabled={!canUndo} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="parts-undo-btn"><ArrowArcLeft size={13} className="mr-1" /> {t("parts.undo")}</Button>
            <Button onClick={onRedo} disabled={!canRedo} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="parts-redo-btn"><ArrowArcRight size={13} /></Button>
            <button onClick={() => setIsolate((v) => !v)} className={`text-xs px-3 py-1 rounded-full border flex items-center gap-1 ${isolate ? "border-gold text-gold bg-gold/10" : "border-white/15 text-neutral-400"}`} data-testid="isolate-toggle"><Eye size={13} /> {t("parts.isolate")}</button>
            <button onClick={() => setPlaceMode((v) => !v)} className={`text-xs px-3 py-1 rounded-full border flex items-center gap-1 ${placeMode ? "border-cyan-400 text-cyan-300 bg-cyan-400/10" : "border-white/15 text-neutral-400"}`} data-testid="place-on-band-toggle"><Crosshair size={13} /> {t("parts.stickToSurface")}</button>
            {fullscreen && (
              <Button onClick={() => setMenuOpen((v) => !v)} size="sm" variant="outline" className="rounded-full border-white/15 h-7 text-xs" data-testid="menu-toggle">
                {menuOpen ? <><CaretDoubleRight size={13} className="mr-1" /> {t("parts.hideMenu")}</> : <><CaretDoubleLeft size={13} className="mr-1" /> {t("parts.showMenu")}</>}
              </Button>
            )}
            {selId && <span className="text-[11px] text-neutral-500 ml-1">{t("parts.editing")} <b className="text-neutral-300">{(parts || []).find((p) => p.id === selId)?.name}</b></span>}
            {groupSel.length >= 1 && (
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-[11px] text-cyan-400">{t("parts.marked", { n: groupSel.length })}</span>
                <Button onClick={groupSelected} disabled={groupSel.length < 2} size="sm" className="rounded-full bg-cyan-500 text-black hover:bg-cyan-400 h-7 text-xs font-semibold" data-testid="group-toolbar-btn"><LinkSimple size={13} className="mr-1" /> {t("parts.groupInto1")}</Button>
                <button onClick={() => setGroupSel([])} className="text-[11px] text-neutral-400 hover:text-neutral-200">{t("parts.clear")}</button>
              </div>
            )}
          </div>
          <div className="rounded-lg overflow-hidden bg-neutral-200 relative" style={{ height: fullscreen ? "72vh" : 340 }} data-testid="parts-canvas">
            <Canvas camera={{ position: [0, 1.2, 4], fov: 45 }} gl={{ preserveDrawingBuffer: true }}
              onPointerMissed={() => { if (placeMode || draggingRef.current) return; setSelId(null); setGroupSel([]); }}>
              <color attach="background" args={["#e9e7e3"]} />
              <ambientLight intensity={0.6} />
              <directionalLight position={[5, 8, 5]} intensity={1.6} />
              <Environment resolution={128} frames={1}>
                <Lightformer form="rect" intensity={3} position={[0, 5, -6]} scale={[10, 6, 1]} />
                <Lightformer form="rect" intensity={2} position={[-5, 1, 4]} scale={[8, 6, 1]} />
              </Environment>
              <gridHelper args={[10, 20, "#b9b6b0", "#d4d1cb"]} position={[0, -1.4, 0]} />
              <axesHelper args={[1.6]} />
              <Suspense fallback={<Html center><CircleNotch className="animate-spin" size={24} /></Html>}>
                <Bounds key={frameKey} fit margin={1.3}>
                  <group ref={sceneGroupRef}>
                    {(parts || []).map((p) => (
                      <EditablePart key={p.id} part={p} selected={selId === p.id} mode={mode} mainFamily={materialFamily}
                        dim={isolate && selId && selId !== p.id} onCommit={(t, st) => applyTransform(p.id, t, st)} onRef={onRef} onSelect={selectPart} marked={groupSel.includes(p.id)}
                        placeMode={placeMode} onPlaceClick={placeOnBand}
                        onDragStart={() => { draggingRef.current = true; }} onDragEnd={() => { setTimeout(() => { draggingRef.current = false; }, 0); }} />
                    ))}
                  </group>
                </Bounds>
              </Suspense>
              <OrbitControls makeDefault enablePan />
              <ViewController viewRef={viewRef} />
            </Canvas>
            {(parts || []).length === 0 && <div className="absolute inset-0 flex items-center justify-center text-sm text-neutral-500 pointer-events-none">{t("parts.noParts")}</div>}
            {placeMode && (
              <div className="absolute top-2 left-1/2 -translate-x-1/2 bg-cyan-500 text-black text-xs font-semibold px-3 py-1.5 rounded-full flex items-center gap-1.5 pointer-events-none shadow-lg" data-testid="place-mode-hint">
                <Crosshair size={14} /> {selId && !isBand((parts || []).find((p) => p.id === selId) || {})
                  ? t("parts.placeHintSelected")
                  : t("parts.placeHintSelect")}
              </div>
            )}
            {selId && (() => {
              const p = (parts || []).find((x) => x.id === selId);
              if (!p) return null;
              const hasRadius = ["torus", "cylinder", "cone", "prism"].includes(p.primitive);
              return (
                <div className="absolute top-2 right-2 w-56 max-h-[92%] overflow-y-auto bg-[#161616]/95 backdrop-blur border border-white/15 rounded-lg p-3 space-y-2 text-neutral-200 shadow-2xl" data-testid="floating-props" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gold truncate">{p.name}</span>
                    <span className="text-[9px] text-neutral-500 uppercase">{p.primitive || "modelo"}</span>
                  </div>
                  <div className="flex gap-1">
                    {[["translate", ArrowsOutCardinal], ["rotate", ArrowClockwise], ["scale", ArrowsOut]].map(([m, Icon]) => (
                      <button key={m} onClick={() => setMode(m)} className={`flex-1 py-1 rounded border flex items-center justify-center ${mode === m ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400"}`} data-testid={`float-gizmo-${m}`}><Icon size={13} /></button>
                    ))}
                  </div>
                  <div className="text-[10px] text-neutral-400">Curvar eje X · {Math.round((p.bendX ?? ((p.bendAxis || "x") !== "y" ? (p.bend || 0) : 0)) * 100)}%</div>
                  <input type="range" min="-1" max="1" step="0.05" value={p.bendX ?? ((p.bendAxis || "x") !== "y" ? (p.bend || 0) : 0)} onChange={(e) => updPart(p.id, { bendX: parseFloat(e.target.value), bendY: p.bendY ?? ((p.bendAxis || "x") === "y" ? (p.bend || 0) : 0), bend: undefined, bendAxis: undefined })} className="w-full accent-[#d4af37]" data-testid="float-bend-x" />
                  <div className="text-[10px] text-neutral-400">Curvar eje Y · {Math.round((p.bendY ?? ((p.bendAxis || "x") === "y" ? (p.bend || 0) : 0)) * 100)}%</div>
                  <input type="range" min="-1" max="1" step="0.05" value={p.bendY ?? ((p.bendAxis || "x") === "y" ? (p.bend || 0) : 0)} onChange={(e) => updPart(p.id, { bendY: parseFloat(e.target.value), bendX: p.bendX ?? ((p.bendAxis || "x") !== "y" ? (p.bend || 0) : 0), bend: undefined, bendAxis: undefined })} className="w-full accent-[#d4af37]" data-testid="float-bend-y" />
                  <div className="text-[10px] text-neutral-400">Curvar eje Z (en el plano) · {Math.round((p.bendZ || 0) * 100)}%</div>
                  <input type="range" min="-1" max="1" step="0.05" value={p.bendZ || 0} onChange={(e) => updPart(p.id, { bendZ: parseFloat(e.target.value) })} className="w-full accent-[#d4af37]" data-testid="float-bend-z" />
                  <div className="text-[10px] text-neutral-400">Nº de ondas suaves (X · Y · Z)</div>
                  <div className="flex gap-1">
                    {["curvesX", "curvesY", "curvesZ"].map((k) => (
                      <input key={k} type="number" min="1" max="8" value={p[k] || 1} onChange={(e) => updPart(p.id, { [k]: Math.max(1, Math.min(8, parseInt(e.target.value) || 1)) })} className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-center text-neutral-200" data-testid={`float-${k}`} />
                    ))}
                  </div>
                  {!p.primitive && (
                    <>
                      <div className="text-[10px] text-neutral-400">Tamaño (llena el área) · {(p.scale?.[0] ?? 1).toFixed(2)}</div>
                      <input type="range" min="0.15" max="1.8" step="0.02" value={p.scale?.[0] ?? 1}
                        onChange={(e) => { const v = parseFloat(e.target.value); updPart(p.id, { scale: [v, v, p.scale?.[2] ?? 0.6] }); }}
                        className="w-full accent-[#d4af37]" data-testid="float-size" />
                      {p.role === "logo" && (
                        <>
                          <div className="text-[10px] text-neutral-400">Grosor del relieve · {((p.reliefDepth ?? 0.09) * 100).toFixed(0)}</div>
                          <input type="range" min="0.02" max="0.4" step="0.01" value={p.reliefDepth ?? 0.09}
                            onChange={(e) => updPart(p.id, { reliefDepth: parseFloat(e.target.value) })}
                            className="w-full accent-[#d4af37]" data-testid="float-relief-depth" />
                          <div className="text-[10px] text-neutral-400 mt-1">Dimensiones</div>
                          <select value={p.sizeMode || "ratio"} onChange={(e) => updPart(p.id, { sizeMode: e.target.value })}
                            className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-neutral-200" data-testid="float-size-mode">
                            <option value="ratio">Ratio (llena la zona)</option>
                            <option value="abs">Medida exacta</option>
                          </select>
                          {(p.sizeMode || "ratio") === "ratio" ? (
                            <>
                              <div className="text-[10px] text-neutral-400 mt-1">Ajustar por</div>
                              <select value={p.fitAxis || "longest"} onChange={(e) => updPart(p.id, { fitAxis: e.target.value })}
                                className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-neutral-200" data-testid="float-fit-axis">
                                <option value="longest">Lado más largo</option>
                                <option value="width">Ancho</option>
                                <option value="height">Alto</option>
                              </select>
                              <div className="grid grid-cols-2 gap-1 mt-1">
                                <div><div className="text-[10px] text-neutral-400">Ancho ×</div>
                                  <input type="number" min="0.2" max="3" step="0.05" value={p.ratioW ?? 1} onChange={(e) => updPart(p.id, { ratioW: parseFloat(e.target.value) || 1 })} className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-center text-neutral-200" data-testid="float-ratio-w" /></div>
                                <div><div className="text-[10px] text-neutral-400">Alto ×</div>
                                  <input type="number" min="0.2" max="3" step="0.05" value={p.ratioH ?? 1} onChange={(e) => updPart(p.id, { ratioH: parseFloat(e.target.value) || 1 })} className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-center text-neutral-200" data-testid="float-ratio-h" /></div>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="text-[10px] text-neutral-400 mt-1">Unidad</div>
                              <select value={p.sizeUnit || "mm"} onChange={(e) => updPart(p.id, { sizeUnit: e.target.value })}
                                className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-neutral-200" data-testid="float-size-unit">
                                <option value="mm">mm</option><option value="cm">cm</option><option value="in">pulgadas</option>
                              </select>
                              <div className="grid grid-cols-3 gap-1 mt-1">
                                <div><div className="text-[10px] text-neutral-400">Ancho</div>
                                  <input type="number" min="0.1" step="0.1" value={p.absW ?? 8} onChange={(e) => updPart(p.id, { absW: parseFloat(e.target.value) || 0 })} className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-center text-neutral-200" data-testid="float-abs-w" /></div>
                                <div><div className="text-[10px] text-neutral-400">Alto</div>
                                  <input type="number" min="0.1" step="0.1" value={p.absH ?? 8} onChange={(e) => updPart(p.id, { absH: parseFloat(e.target.value) || 0 })} className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-center text-neutral-200" data-testid="float-abs-h" /></div>
                                <div><div className="text-[10px] text-neutral-400">Grosor</div>
                                  <input type="number" min="0.1" step="0.1" value={p.absD ?? 3} onChange={(e) => updPart(p.id, { absD: parseFloat(e.target.value) || 0 })} className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-center text-neutral-200" data-testid="float-abs-d" /></div>
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}
                  {p.primitive === "box" && (
                    <>
                      <div className="text-[10px] text-neutral-400">Trapecio · ancho superior · {Math.round((p.taperTop ?? p.taper ?? 0) * 100)}%</div>
                      <input type="range" min="0" max="1" step="0.05" value={p.taperTop ?? p.taper ?? 0} onChange={(e) => updPart(p.id, { taperTop: parseFloat(e.target.value), taper: undefined })} className="w-full accent-[#d4af37]" data-testid="float-taper-top" />
                      <div className="text-[10px] text-neutral-400">Trapecio · ancho inferior · {Math.round((p.taperBottom || 0) * 100)}%</div>
                      <input type="range" min="0" max="1" step="0.05" value={p.taperBottom || 0} onChange={(e) => updPart(p.id, { taperBottom: parseFloat(e.target.value) })} className="w-full accent-[#d4af37]" data-testid="float-taper-bottom" />
                      <div className="text-[10px] text-neutral-400">Paralelogramo (inclinar) · {Math.round((p.shear || 0) * 100)}%</div>
                      <input type="range" min="-1" max="1" step="0.05" value={p.shear || 0} onChange={(e) => updPart(p.id, { shear: parseFloat(e.target.value) })} className="w-full accent-[#d4af37]" data-testid="float-shear" />
                    </>
                  )}
                  {p.role === "stones" && (
                    <div>
                      <div className="text-[10px] text-neutral-400 mb-1">Set de piedras (color independiente en el personalizador)</div>
                      <input type="text" value={p.stoneSet || ""} placeholder="1" onChange={(e) => updPart(p.id, { stoneSet: e.target.value.trim() })} data-testid="float-stone-set" className="w-full bg-white/5 border border-white/15 rounded h-7 px-2 text-[11px] text-neutral-200" />
                    </div>
                  )}
                  {p.primitive === "gem" && (
                    <div>
                      <div className="text-[10px] text-neutral-400 mb-1">Forma de gema (corte)</div>
                      <select value={p.dims?.cut || "round"} onChange={(e) => updPart(p.id, { dims: { ...(p.dims || {}), cut: e.target.value } })} data-testid="float-gem-cut" className="w-full bg-white/5 border border-white/15 rounded h-7 text-[11px] text-neutral-200">
                        {STONE_SHAPES.map((c) => (<option key={c.key} value={c.key}>{c.label}</option>))}
                      </select>
                    </div>
                  )}
                  {hasRadius && (
                    <>
                      {p.primitive === "prism" ? (
                        <>
                          <div className="text-[10px] text-neutral-400">Radio arriba · {(p.dims?.radiusTop ?? 0.5).toFixed(2)}</div>
                          <input type="range" min="0.02" max="1" step="0.02" value={p.dims?.radiusTop ?? 0.5} onChange={(e) => setDim(p, "radiusTop", e.target.value)} className="w-full accent-[#d4af37]" data-testid="float-radiustop" />
                          <div className="text-[10px] text-neutral-400">Radio abajo · {(p.dims?.radiusBottom ?? 0.5).toFixed(2)}</div>
                          <input type="range" min="0.02" max="1" step="0.02" value={p.dims?.radiusBottom ?? 0.5} onChange={(e) => setDim(p, "radiusBottom", e.target.value)} className="w-full accent-[#d4af37]" data-testid="float-radiusbottom" />
                        </>
                      ) : (
                        <>
                          <div className="text-[10px] text-neutral-400">Radio · {(p.dims?.radius ?? 0.5).toFixed(2)}</div>
                          <input type="range" min="0.1" max="1" step="0.02" value={p.dims?.radius ?? 0.5} onChange={(e) => setDim(p, "radius", e.target.value)} className="w-full accent-[#d4af37]" data-testid="float-radius" />
                        </>
                      )}
                      {p.primitive === "torus" ? (
                        <>
                          <div className="text-[10px] text-neutral-400">Grosor · {(p.dims?.tube ?? 0.18).toFixed(2)}</div>
                          <input type="range" min="0.03" max="0.45" step="0.01" value={p.dims?.tube ?? 0.18} onChange={(e) => setDim(p, "tube", e.target.value)} className="w-full accent-[#d4af37]" data-testid="float-tube" />
                        </>
                      ) : (
                        <>
                          <div className="text-[10px] text-neutral-400">Alto · {(p.dims?.height ?? 1).toFixed(2)}</div>
                          <input type="range" min="0.1" max="2" step="0.05" value={p.dims?.height ?? 1} onChange={(e) => setDim(p, "height", e.target.value)} className="w-full accent-[#d4af37]" data-testid="float-height" />
                        </>
                      )}
                      {p.primitive === "prism" && (
                        <>
                          <div className="text-[10px] text-neutral-400">Lados · {Math.round(p.dims?.sides ?? 3)}</div>
                          <input type="range" min="3" max="8" step="1" value={p.dims?.sides ?? 3} onChange={(e) => setDim(p, "sides", e.target.value)} className="w-full accent-[#d4af37]" data-testid="float-sides" />
                        </>
                      )}
                    </>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <input type="color" value={p.color || "#C0C0C0"} onChange={(e) => updPart(p.id, { color: e.target.value })} className="w-7 h-7 rounded border border-white/15 bg-transparent cursor-pointer" data-testid="float-color" />
                    <span className="text-[10px] text-neutral-500">Color / metal</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1">
                    <Button onClick={() => alignToBand(p)} size="sm" variant="outline" className="rounded-full border-gold/40 text-gold h-7 text-[10px] px-1" data-testid="float-align"><MagicWand size={12} className="mr-1" /> Alinear</Button>
                    <Button onClick={() => realCurve(p)} size="sm" variant="outline" className="rounded-full border-gold/40 text-gold h-7 text-[10px] px-1" data-testid="float-realcurve"><ArrowClockwise size={12} className="mr-1" /> Curva real</Button>
                  </div>
                  {p.group_id && (
                    <Button onClick={() => ungroup(p)} size="sm" variant="outline" className="w-full rounded-full border-white/15 text-neutral-300 h-7 text-[10px]" data-testid="float-ungroup"><LinkBreak size={12} className="mr-1" /> Desagrupar</Button>
                  )}
                </div>
              );
            })()}
          </div>
        </div>

        <div className={`${fullscreen && !menuOpen ? "hidden" : ""} space-y-2 ${fullscreen ? "overflow-y-auto pr-1" : ""}`} style={fullscreen ? { maxHeight: "72vh" } : undefined}>
          {selId && (
            <div className="flex gap-1 mb-2">
              {[["translate", ArrowsOutCardinal, t("parts.move")], ["rotate", ArrowClockwise, t("parts.rotate")], ["scale", ArrowsOut, t("parts.scale")]].map(([m, Icon, lbl]) => (
                <button key={m} onClick={() => setMode(m)} className={`flex-1 text-xs py-1.5 rounded border flex items-center justify-center gap-1 ${mode === m ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-neutral-400"}`} data-testid={`gizmo-${m}`}><Icon size={13} /> {lbl}</button>
              ))}
            </div>
          )}
          {groupSel.length >= 1 && (
            <div className="flex items-center gap-2 mb-2 p-2 rounded-lg border border-gold/30 bg-gold/5">
              <span className="text-[11px] text-gold flex-1">{t("parts.markedToJoin", { n: groupSel.length })}</span>
              <Button onClick={groupSelected} disabled={groupSel.length < 2} size="sm" className="rounded-full bg-gold text-obsidian hover:bg-gold/90 h-7 text-[11px] font-semibold" data-testid="group-selected-btn"><LinkSimple size={13} className="mr-1" /> {t("parts.groupInto1")}</Button>
              <button onClick={() => setGroupSel([])} className="text-[11px] text-neutral-400 hover:text-neutral-200">{t("parts.clear")}</button>
            </div>
          )}
          {(parts || []).length === 0 && <p className="text-sm text-neutral-500">{t("parts.noPartsShort")}</p>}
          {(parts || []).map((p) => {
            const selGid = (parts || []).find((x) => x.id === selId)?.group_id;
            const inSelGroup = p.group_id && p.group_id === selGid;
            return (
            <div key={p.id} onClick={() => setSelId(p.id)} data-testid={`part-row-${p.id}`}
              className={`rounded-lg p-3 cursor-pointer border ${selId === p.id ? "border-gold bg-gold/5" : inSelGroup ? "border-gold/40 bg-gold/[0.03]" : "border-white/10 bg-white/5"}`}>
              <div className="flex items-center gap-2">
                <input type="checkbox" title={t("parts.groupInto1")} checked={groupSel.includes(p.id)} onChange={() => toggleGroupSel(p.id)} onClick={(e) => e.stopPropagation()} className="accent-[#d4af37]" data-testid={`part-groupsel-${p.id}`} />
                <PartRowThumb part={p} />
                <Input value={p.name} onChange={(e) => updPart(p.id, { name: e.target.value })} onClick={(e) => { e.stopPropagation(); setSelId(p.id); }} onFocus={() => setSelId(p.id)} className="bg-white/5 border-white/15 h-8 text-sm flex-1" data-testid={`part-name-${p.id}`} />
                {p.group_id && <span title={t("parts.ungroup")} className="text-gold shrink-0"><LinkSimple size={14} /></span>}
                {p.slot_anchor && <span title={t("parts.slotTemplate")} className="text-cyan-400 shrink-0"><Crosshair size={14} /></span>}
                <button onClick={(e) => { e.stopPropagation(); dupPart(p); }} title={t("parts.t_duplicated")} className="text-neutral-500 hover:text-gold" data-testid={`part-duplicate-${p.id}`}><Copy size={15} /></button>
                <button onClick={(e) => { e.stopPropagation(); delPart(p.id); }} className="text-neutral-500 hover:text-red-400"><Trash size={15} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                <Select value={p.role} onValueChange={(v) => updPart(p.id, { role: v, normalize: false })}>
                  <SelectTrigger className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`part-role-${p.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{t(`parts.role_${r}`)}</SelectItem>)}</SelectContent>
                </Select>
                {p.role === "logo" ? (
                  <Select value={["left", "right", "center", "top", "bottom", "topright", "topleft"].includes(p.slot) ? p.slot : "center"} onValueChange={(v) => updPart(p.id, { slot: v })}>
                    <SelectTrigger className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`part-slot-${p.id}`}><SelectValue placeholder={t("parts.zone")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="left">{t("parts.zones.left")}</SelectItem>
                      <SelectItem value="right">{t("parts.zones.right")}</SelectItem>
                      <SelectItem value="center">{t("parts.zones.center")}</SelectItem>
                      <SelectItem value="top">{t("parts.zones.top")}</SelectItem>
                      <SelectItem value="bottom">{t("parts.zones.bottom")}</SelectItem>
                      <SelectItem value="topright">{t("parts.zones.topright")}</SelectItem>
                      <SelectItem value="topleft">{t("parts.zones.topleft")}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={p.slot} onChange={(e) => updPart(p.id, { slot: e.target.value })} placeholder={t("parts.slotPlaceholder")} className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`part-slot-${p.id}`} />
                )}
              </div>
              <div className="flex items-center gap-3 mt-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                <input type="color" value={p.color || "#C0C0C0"} onChange={(e) => updPart(p.id, { color: e.target.value })} className="w-7 h-7 rounded border border-white/15 bg-transparent cursor-pointer" data-testid={`part-color-${p.id}`} />
                <Select value={p.material || "inherit"} onValueChange={(v) => updPart(p.id, { material: v === "inherit" ? null : v })}>
                  <SelectTrigger className="bg-white/5 border-white/15 h-8 text-xs w-32" data-testid={`part-material-${p.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inherit">{t("parts.materialInherit")}</SelectItem>
                    {FAMILY_ORDER.map((f) => <SelectItem key={f} value={f}>{MATERIAL_FAMILIES[f].label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1.5 text-xs text-neutral-400"><input type="checkbox" checked={!!p.optional} onChange={(e) => updPart(p.id, { optional: e.target.checked })} /> {t("parts.optional")}</label>
                <label className="flex items-center gap-1.5 text-xs text-neutral-400" title={t("parts.t_anchorSet", { name: p.name, slot: p.slot })}>
                  <input type="checkbox" checked={!!p.slot_anchor} onChange={(e) => setAnchor(p, e.target.checked)} data-testid={`part-anchor-${p.id}`} /> {t("parts.slotTemplate")}
                </label>
                {p.role === "logo" && (
                  <label className="flex items-center gap-1.5 text-xs text-neutral-400" title="Redimensiona/centra el logo para rellenar la zona. Desactívalo para dejar la pieza tal cual (no se mueve ni cambia de forma).">
                    <input type="checkbox" checked={p.normalize != null ? !!p.normalize : true} onChange={(e) => updPart(p.id, { normalize: e.target.checked })} data-testid={`part-normalize-${p.id}`} /> Rellenar zona
                  </label>
                )}
              </div>
              {selId === p.id && (
                <div className="mt-3 border-t border-white/10 pt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                  <div className="text-[11px] text-neutral-400 uppercase tracking-wide">{t("parts.dims")}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[t("parts.width"), t("parts.hgt"), t("parts.depth")].map((lbl, idx) => {
                      const isLogoDepth = idx === 2 && p.role === "logo" && (p.normalize == null ? true : !!p.normalize);
                      if (isLogoDepth) {
                        return (
                          <div key={idx}>
                            <label className="text-[10px] text-neutral-500">{lbl}</label>
                            <Input type="number" step="0.01" min="0.02" max="0.6" value={Number((p.reliefDepth ?? 0.09).toFixed(3))}
                              onChange={(e) => updPart(p.id, { reliefDepth: Math.max(0.01, Math.min(0.6, parseFloat(e.target.value) || 0.09)) })}
                              className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`part-depth-relief-${p.id}`} title="Grosor/profundidad del logo (se mantiene aunque cambies el tamaño)" />
                          </div>
                        );
                      }
                      return (
                        <div key={idx}>
                          <label className="text-[10px] text-neutral-500">{lbl}</label>
                          <Input type="number" step="0.05" min="0.05" value={Number(asScale3(p.scale)[idx].toFixed(3))} onChange={(e) => setScaleAxis(p, idx, e.target.value)} className="bg-white/5 border-white/15 h-8 text-xs" data-testid={`part-scale-${idx}-${p.id}`} />
                        </div>
                      );
                    })}
                  </div>
                  <div className="border-t border-white/10 pt-2">
                    <div className="text-[11px] text-neutral-400 uppercase tracking-wide mb-1">{t("parts.frontOrient")}</div>
                    <div className="text-[10px] text-neutral-500 mb-1.5">{t("parts.frontOrientHint")}</div>
                    <div className="grid grid-cols-5 gap-1.5">
                      <Button size="sm" variant="outline" className="rounded border-white/15 h-8 text-[11px] px-1" onClick={() => rotOrient(p, 1, Math.PI / 2)} data-testid={`part-orient-y-${p.id}`}>{t("parts.turnY")}</Button>
                      <Button size="sm" variant="outline" className="rounded border-white/15 h-8 text-[11px] px-1" onClick={() => rotOrient(p, 0, Math.PI / 2)} data-testid={`part-orient-x-${p.id}`}>{t("parts.turnX")}</Button>
                      <Button size="sm" variant="outline" className="rounded border-white/15 h-8 text-[11px] px-1" onClick={() => rotOrient(p, 2, Math.PI / 2)} data-testid={`part-orient-z-${p.id}`}>{t("parts.turnZ")}</Button>
                      <Button size="sm" variant="outline" className="rounded border-white/15 h-8 text-[11px] px-1" onClick={() => rotOrient(p, 1, Math.PI)} data-testid={`part-orient-180-${p.id}`}>{t("parts.turn180")}</Button>
                      <Button size="sm" variant="outline" className="rounded border-gold/40 text-gold h-8 text-[11px] px-1" onClick={() => updPart(p.id, { orient: [0, 0, 0], autoOrient: false })} data-testid={`part-orient-reset-${p.id}`}>{t("parts.straighten")}</Button>
                    </div>
                  </div>
                  {p.role === "logo" && p.model_url && (
                    <div className="border-t border-white/10 pt-2">
                      <Button size="sm" variant="outline" className="w-full rounded border-gold/40 text-gold h-8 text-[11px]" onClick={() => addPartToCollection(p)} data-testid={`part-to-collection-${p.id}`}>
                        <Cube size={13} className="mr-1" /> Añadir a la colección de logos
                      </Button>
                      <div className="text-[10px] text-neutral-500 mt-1">Se guarda tal cual (curva y grosor incluidos) para reusarlo e intercambiarlo en otras sortijas.</div>
                    </div>
                  )}
                  {((p.primitive && !["torus", "sphere", "gem"].includes(p.primitive)) || (p.role === "logo" && p.model_url)) && (
                    <div className="border-t border-white/10 pt-2 space-y-1.5">
                      <div className="text-[11px] text-neutral-400 uppercase tracking-wide">{t("parts.trapezoid")}</div>
                      {[["taperTop", "trapTop"], ["taperBottom", "trapBottom"], ["taperLeft", "trapLeft"], ["taperRight", "trapRight"]].map(([key, lbl]) => (
                        <div key={key}>
                          <div className="flex items-center justify-between"><label className="text-[10px] text-neutral-500">{t(`parts.${lbl}`)}</label><span className="text-[10px] text-neutral-500">{Math.round((p[key] || 0) * 100)}%</span></div>
                          <input type="range" min="0" max="0.9" step="0.05" value={p[key] || 0} onChange={(e) => updPart(p.id, { [key]: parseFloat(e.target.value) })} className="w-full accent-[#d4af37]" data-testid={`part-${key.toLowerCase()}-${p.id}`} />
                        </div>
                      ))}
                    </div>
                  )}
                  {(p.primitive === "torus" || p.primitive === "cylinder") && (
                    <div className="space-y-1.5 border-t border-white/10 pt-2">
                      <div className="flex items-center justify-between"><label className="text-[11px] text-neutral-400">{t("parts.ringRadius")}</label><span className="text-[10px] text-neutral-500">{(p.dims?.radius ?? 0.5).toFixed(2)}</span></div>
                      <input type="range" min="0.2" max="0.8" step="0.02" value={p.dims?.radius ?? 0.5} onChange={(e) => setDim(p, "radius", e.target.value)} className="w-full accent-[#d4af37]" data-testid={`part-radius-${p.id}`} />
                      <div className="flex items-center justify-between"><label className="text-[11px] text-neutral-400">{t("parts.bandThickness")}</label><span className="text-[10px] text-neutral-500">{(p.primitive === "torus" ? (p.dims?.tube ?? 0.18) : (p.dims?.height ?? 1)).toFixed(2)}</span></div>
                      {p.primitive === "torus"
                        ? <input type="range" min="0.03" max="0.45" step="0.01" value={p.dims?.tube ?? 0.18} onChange={(e) => setDim(p, "tube", e.target.value)} className="w-full accent-[#d4af37]" data-testid={`part-tube-${p.id}`} />
                        : <input type="range" min="0.1" max="2" step="0.05" value={p.dims?.height ?? 1} onChange={(e) => setDim(p, "height", e.target.value)} className="w-full accent-[#d4af37]" data-testid={`part-height-${p.id}`} />}
                    </div>
                  )}
                  <div className="text-[11px] text-neutral-400 uppercase tracking-wide">{t("parts.bendFlat")}</div>
                  <div className="text-[10px] text-neutral-400 mt-1">{t("parts.axisX")} · {Math.round((p.bendX ?? ((p.bendAxis || "x") !== "y" ? (p.bend || 0) : 0)) * 100)}%</div>
                  <input type="range" min="-1" max="1" step="0.05" value={p.bendX ?? ((p.bendAxis || "x") !== "y" ? (p.bend || 0) : 0)} onChange={(e) => updPart(p.id, { bendX: parseFloat(e.target.value), bendY: p.bendY ?? ((p.bendAxis || "x") === "y" ? (p.bend || 0) : 0), bend: undefined, bendAxis: undefined })} className="w-full accent-[#d4af37]" data-testid={`part-bend-x-${p.id}`} />
                  <div className="text-[10px] text-neutral-400 mt-1">{t("parts.axisY")} · {Math.round((p.bendY ?? ((p.bendAxis || "x") === "y" ? (p.bend || 0) : 0)) * 100)}%</div>
                  <input type="range" min="-1" max="1" step="0.05" value={p.bendY ?? ((p.bendAxis || "x") === "y" ? (p.bend || 0) : 0)} onChange={(e) => updPart(p.id, { bendY: parseFloat(e.target.value), bendX: p.bendX ?? ((p.bendAxis || "x") !== "y" ? (p.bend || 0) : 0), bend: undefined, bendAxis: undefined })} className="w-full accent-[#d4af37]" data-testid={`part-bend-y-${p.id}`} />
                  <div className="text-[10px] text-neutral-400 mt-1">{t("parts.axisZ")} · {Math.round((p.bendZ || 0) * 100)}%</div>
                  <input type="range" min="-1" max="1" step="0.05" value={p.bendZ || 0} onChange={(e) => updPart(p.id, { bendZ: parseFloat(e.target.value) })} className="w-full accent-[#d4af37]" data-testid={`part-bend-z-${p.id}`} />
                  <div className="text-[10px] text-neutral-400 mt-1">{t("parts.curvesCount")} (X · Y · Z)</div>
                  <div className="flex gap-1.5 mt-1">
                    {["curvesX", "curvesY", "curvesZ"].map((k) => (
                      <input key={k} type="number" min="1" max="8" value={p[k] || 1} onChange={(e) => updPart(p.id, { [k]: Math.max(1, Math.min(8, parseInt(e.target.value) || 1)) })} className="w-full bg-white/5 border border-white/15 rounded h-8 text-xs text-center text-neutral-200" data-testid={`part-${k.toLowerCase()}-${p.id}`} />
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mt-1">
                    <Button onClick={() => alignToBand(p)} size="sm" variant="outline" className="rounded-full border-gold/40 text-gold h-8 text-xs" data-testid={`part-align-${p.id}`}><MagicWand size={13} className="mr-1" /> {t("parts.alignToRing")}</Button>
                    <Button onClick={() => realCurve(p)} size="sm" variant="outline" className="rounded-full border-gold/40 text-gold h-8 text-xs" data-testid={`part-realcurve-${p.id}`}><ArrowClockwise size={13} className="mr-1" /> {t("parts.realCurveFull")}</Button>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-neutral-400 cursor-pointer mt-1" data-testid={`part-centered-${p.id}`}>
                    <input type="checkbox" checked={!!p.centered} onChange={() => toggleCentered(p)} /> {t("parts.scaleFromCenter")}
                  </label>
                </div>
              )}
            </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
